import { TAGS_API } from 'modules/tags/api/tags-api'
import { TagFilterInterface } from 'modules/tags/types/tag-filter-interface'
import { TagApiInterface, TagStatisticApiInterface } from 'modules/tags/types/tag-interface'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import useUser from 'shared/hooks/use-user'
import { CountInterface, SwrPaginationInterface } from 'shared/types/pagination-interface'
import { getFilterQueryString } from 'shared/utils/api/get-filter-query-string'
import { getPaginationAndFilterQueryString } from 'shared/utils/api/get-pagination-and-filter-query-string'
import { baseFetcher } from 'shared/utils/http-client'

interface UseTagsInterface extends SwrPaginationInterface {
  filter: TagFilterInterface
}

export function useTags({ limit, pagination, filter }: UseTagsInterface) {
  const { user } = useUser()
  const { revalidateModules } = useRevalidateModules()

  const { fetcher: listFetcher } = useFetch<RequestMethodsEnum.get, TagApiInterface>({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: statisticsFetcher } = useFetch<RequestMethodsEnum.get, TagStatisticApiInterface>(
    {
      method: RequestMethodsEnum.get,
    },
  )

  const { data: tags, mutate } = useControlledSwr(
    () =>
      user && [
        `${TAGS_API}/listing?${getPaginationAndFilterQueryString({
          pagination,
          limit,
          filter: { ...filter },
        })}`,
        RequestModuleEnum.Tags,
        RequestModuleEnum.TagsListing,
      ],
    listFetcher,
    undefined,
    undefined,
  )

  const { data: tagsStatistics } = useControlledSwr(
    () =>
      user && [
        `${TAGS_API}/statistics?${getPaginationAndFilterQueryString({
          pagination,
          limit,
          filter: { ...filter },
        })}`,
        RequestModuleEnum.Tags,
        RequestModuleEnum.TagsStatistics,
      ],
    statisticsFetcher,
    undefined,
    undefined,
  )

  const { data: counter } = useControlledSwr<CountInterface>(
    () =>
      user && [
        `${TAGS_API}/count?${getFilterQueryString({ filter: { ...filter } })}`,
        RequestModuleEnum.Tags,
        RequestModuleEnum.TagsCounter,
      ],
    baseFetcher,
    undefined,
    undefined,
  )

  const revalidateTagsModule = (hardRevalidate?: boolean) => {
    revalidateModules([RequestModuleEnum.Tags], hardRevalidate)
  }

  return { tags, tagsStatistics, mutate, counter, revalidateTagsModule }
}
